import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    id: "overview",
    title: "1. Overview",
    body: [
      "Flappy Goal (\"the Game\") is a free-to-play mobile arcade game. This Privacy Policy explains what information the Game handles, why, and the choices you have. By playing the Game you agree to this policy.",
      "The short version: we collect as little as possible, we never sell your data, and most of the Game works fully offline.",
    ],
  },
  {
    id: "data-we-collect",
    title: "2. Data We Collect",
    body: [
      "Gameplay data stored on your device: high scores, unlocked skins, settings and progress. This never leaves your phone unless you opt in to online features.",
      "Online features (optional): if you use leaderboards or daily events, we store a pseudonymous player ID, your chosen nickname, and your scores.",
      "Contact form: if you write to us through this website, we store your name, email address and message so we can reply.",
      "We do not collect your precise location, contacts, photos, microphone or camera data.",
    ],
  },
  {
    id: "how-we-use",
    title: "3. How We Use Data",
    body: [
      "To run the Game: saving progress, syncing scores to leaderboards and delivering daily events.",
      "To improve the Game: aggregated, anonymised crash reports and performance metrics help us fix bugs and balance difficulty.",
      "To communicate: replying to messages you send us through the contact form or by email.",
    ],
  },
  {
    id: "analytics-ads",
    title: "4. Analytics & Advertising",
    body: [
      "We use privacy-respecting, aggregated analytics to understand level completion and crash rates. Analytics data cannot be used to identify you personally.",
      "The Game may show optional rewarded ads (for example, watch an ad to continue a run). We do not build advertising profiles of players.",
    ],
  },
  {
    id: "children",
    title: "5. Children's Privacy",
    body: [
      "Flappy Goal is designed to be family-friendly. We do not knowingly collect personal information from children under 13. Online features use a random player ID rather than personal details.",
      "If you believe a child has provided us personal information, contact us and we will delete it promptly.",
    ],
  },
  {
    id: "third-parties",
    title: "6. Third-Party Services",
    body: [
      "App Store and Google Play: purchases and downloads are handled by Apple and Google under their own privacy policies.",
      "We do not share, sell or rent your personal data to any third party for marketing purposes.",
    ],
  },
  {
    id: "changes",
    title: "7. Changes to This Policy",
    body: [
      "If we update this policy we will post the new version on this page and update the date below. Significant changes will also be announced in-game.",
    ],
  },
  {
    id: "contact",
    title: "8. Contact Us",
    body: [
      "Questions about privacy? Email hello@flappygoal.game or use the Contact Us page. We aim to respond within two working days.",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="pt-36">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-4">
            Legal
          </p>
          <h1 className="font-display text-6xl sm:text-7xl tracking-tight leading-none">
            PRIVACY <span className="text-volt">POLICY</span>
          </h1>
          <p className="mt-5 text-sm font-mono uppercase tracking-widest text-slate-500">
            Last updated: July 2026
          </p>
          <p className="mt-4 text-sm text-slate-400">
            Looking for the Court Vision policy?{" "}
            <Link
              to="/court-vision/privacy"
              data-testid="privacy-crosslink"
              className="inline-flex items-center gap-1.5 text-sky-400 font-semibold hover:underline"
            >
              Court Vision Privacy Policy <ArrowRight size={14} />
            </Link>
          </p>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 grid md:grid-cols-[220px_1fr] gap-12">
        <Reveal>
          <nav
            data-testid="privacy-toc"
            className="md:sticky md:top-28 self-start rounded-2xl border border-line bg-turf/60 p-5"
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">
              Contents
            </p>
            <ul className="space-y-2">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`privacy-toc-${s.id}`}
                    className="text-xs text-slate-400 hover:text-volt transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </Reveal>

        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.id} delay={Math.min(i * 0.05, 0.3)}>
              <section id={s.id} data-testid={`privacy-section-${s.id}`} className="scroll-mt-28">
                <h2 className="font-head text-xl sm:text-2xl font-bold">{s.title}</h2>
                {s.body.map((p, j) => (
                  <p key={j} className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
